/**
 * Atlas focus requests (Tome UI Round 3, the next program's E2): another window asks the World Atlas
 * to show a place, the atlas selects and centres it when it opens or is already open. A request only
 * highlights a destination; it never travels, accepts or rewards anything. Places are the atlas's own
 * (buildWorldMap: drafts and pocket maps are not on it), so a link is offered only where the atlas
 * can show it. A request belongs to the character who made it: this store outlives a roster change,
 * so the next character's atlas drops it instead of opening on someone else's destination.
 */
import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { ZONE_CONFIGS, getZoneConfig } from '../../../../shared/content/generated/zones.gen';
import { LevelRegistry } from '../../../systems/map/LevelRegistry';
import { useOptionalGame } from '../../contexts/GameContext';
import { windowBridge } from '../../framework/windowBridge';
import { isKeyboardFocus } from '../../components/useTouchHoldPin';
import { questJournalOwner } from '../Quest/questJournalFocus';
import { WORLD_MAP_LAYOUT } from './worldMapLayout';
import { buildWorldMap } from './worldMapModel';

export interface AtlasFocusRequest { mapId: string; owner: string; seq: number }

let request: AtlasFocusRequest | null = null;
/** The link a keyboard user pressed, to hand focus back to. A mouse click leaves focus for the game. */
let opener: HTMLElement | null = null;
const listeners = new Set<() => void>();

/** Asks the atlas to show `mapId` for the character `owner` (questJournalOwner), and opens it. */
export function showOnAtlas(mapId: string, owner = ''): void {
  request = { mapId, owner, seq: (request?.seq ?? 0) + 1 };
  const focused = document.activeElement;
  opener = focused instanceof HTMLElement && isKeyboardFocus(focused) ? focused : null;
  listeners.forEach((fn) => fn());
  windowBridge.open('worldMap');
}

/** The atlas closed: focus goes back to the link that opened it, if nothing else has taken it. */
export function returnAtlasFocus(): void {
  if (opener?.isConnected && document.activeElement === document.body) opener.focus();
  opener = null;
}

/** `showOnAtlas` for the character playing now. */
export function useShowOnAtlas(): (mapId: string) => void {
  const owner = questJournalOwner(useOptionalGame()?.player);
  return useCallback((mapId) => showOnAtlas(mapId, owner), [owner]);
}

/** The atlas took the request: a later open shows the current map again. */
export function consumeAtlasFocus(): void {
  if (!request) return;
  request = null;
  listeners.forEach((fn) => fn());
}

/** The pending focus request of the character `owner`, for the atlas to act on and consume. */
export function useAtlasFocusRequest(owner: string): AtlasFocusRequest | null {
  const pending = useSyncExternalStore((fn) => { listeners.add(fn); return () => listeners.delete(fn); }, () => request, () => null);
  useEffect(() => { if (pending && pending.owner !== owner) consumeAtlasFocus(); }, [pending, owner]);
  return pending?.owner === owner ? pending : null;
}

let places: Map<string, string> | null = null;
/** The atlas's places, id → display name (built once, as the atlas builds them). */
function atlasPlaces(): Map<string, string> {
  if (!places) {
    const registry = LevelRegistry.getInstance();
    places = new Map(buildWorldMap(WORLD_MAP_LAYOUT, (id) => registry.getLevel(id), getZoneConfig).places.map((p) => [p.id, p.name]));
  }
  return places;
}

export interface AtlasPlace { mapId: string; name: string }

/** The atlas place for a map, or null when the atlas does not show it. */
export function atlasPlace(mapId: string): AtlasPlace | null {
  const name = atlasPlaces().get(mapId);
  return name === undefined ? null : { mapId, name };
}

/** The atlas place an NPC stands in, or null when it stands on no map the atlas shows. */
export function npcAtlasPlace(npcId: string | null | undefined): AtlasPlace | null {
  if (!npcId) return null;
  for (const [zoneId, zone] of Object.entries(ZONE_CONFIGS)) {
    const place = zone.npcSpawns.some((s) => s.npcId === npcId) ? atlasPlace(zoneId) : null;
    if (place) return place;
  }
  return null;
}

/** For tests: forget the cached places and the last request. */
export function resetAtlasFocusForTests(): void {
  places = null;
  request = null;
  opener = null;
}
